import { SparkLineChart } from '@mui/x-charts/SparkLineChart'
import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { Skeleton } from 'src/components/skeleton'
import EtheriumIcon from '../../../assets/EtheriumIcon.svg'
import USDCIcon from '../../../assets/USDCIcon.svg'
import USDTIcon from '../../../assets/USDTIcon.svg'
import BitcoinIcon from '../../../assets/bitcoinIcon.svg'

interface ApiResponse {
  RAW: {
    [key: string]: {
      USD: {
        PRICE: number
        OPEN24HOUR: number
        HIGH24HOUR: number
        LOW24HOUR: number
      }
    }
  }
}

interface CryptoData {
  icon: string
  symbol: string
  price: string
  chartColor: string
  chartStroke: string
  tendency: 'up' | 'down'
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(value)
}

export function CryptoBoard() {
  const [apiData, setApiData] = useState<ApiResponse | null>(null)
  const [cryptoData, setCryptoData] = useState<CryptoData[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await axios.get<ApiResponse>(
          'https://min-api.cryptocompare.com/data/pricemultifull?fsyms=BTC,ETH,USDC,USDT&tsyms=USD',
          {
            headers: {
              Authorization: `${process.env.NEXT_PUBLIC_CRYPTO_COMPARE_KEY}`,
              'Content-Type': 'application/json',
            },
          }
        )
        setApiData(response.data)
        updateCryptoData(response.data)
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    const interval = setInterval(fetchData, 60000) // Update every minute
    return () => clearInterval(interval)
  }, [])

  const cleanNumericValue = (value: string): string => {
    const regex = /[^0-9.-]/g // Remove tudo que não seja número, ponto ou hífen
    return value.replace(regex, '')
  }

  const updateCryptoData = (data: ApiResponse) => {
    const newCryptoData: CryptoData[] = [
      {
        icon: BitcoinIcon,
        symbol: 'BTC',
        price: formatCurrency(data.RAW.BTC.USD.PRICE),
        chartColor:
          data.RAW.BTC.USD.PRICE >= data.RAW.BTC.USD.OPEN24HOUR
            ? '#79FF86'
            : '#FF4B4A',
        chartStroke: '#F7931A',
        tendency:
          data.RAW.BTC.USD.PRICE >= data.RAW.BTC.USD.OPEN24HOUR ? 'up' : 'down',
      },
      {
        icon: EtheriumIcon,
        symbol: 'ETH',
        price: formatCurrency(data.RAW.ETH.USD.PRICE),
        chartColor:
          data.RAW.ETH.USD.PRICE >= data.RAW.ETH.USD.OPEN24HOUR
            ? '#79FF86'
            : '#FF4B4A',
        chartStroke: '#627EEA',
        tendency:
          data.RAW.ETH.USD.PRICE >= data.RAW.ETH.USD.OPEN24HOUR ? 'up' : 'down',
      },
      {
        icon: USDCIcon,
        symbol: 'USDC',
        price: formatCurrency(data.RAW.USDC.USD.PRICE),
        chartColor:
          data.RAW.USDC.USD.PRICE >= data.RAW.USDC.USD.OPEN24HOUR
            ? '#79FF86'
            : '#FF4B4A',
        chartStroke: '#2775CA',
        tendency:
          data.RAW.USDC.USD.PRICE >= data.RAW.USDC.USD.OPEN24HOUR
            ? 'up'
            : 'down',
      },
      {
        icon: USDTIcon,
        symbol: 'USDT',
        price: formatCurrency(data.RAW.USDT.USD.PRICE),
        chartColor:
          data.RAW.USDT.USD.PRICE >= data.RAW.USDT.USD.OPEN24HOUR
            ? '#79FF86'
            : '#FF4B4A',
        chartStroke: '#26A17B',
        tendency:
          data.RAW.USDT.USD.PRICE >= data.RAW.USDT.USD.OPEN24HOUR
            ? 'up'
            : 'down',
      },
    ]
    setCryptoData(newCryptoData)
  }

  const generateChartData = (symbol: string): number[] => {
    if (!apiData) return [0, 0, 0, 0]

    const currencyData = apiData.RAW[symbol].USD
    return [
      currencyData.OPEN24HOUR,
      currencyData.HIGH24HOUR,
      currencyData.LOW24HOUR,
      currencyData.PRICE,
    ]
  }

  return (
    <div className="flex flex-col w-full h-full">
      <h4 className="font-bold title-text full-hd:mb-2 qhd:mb-4 qhd-plus:mb-4 4k:mb-5 5k:mb-5">
        Crypto
      </h4>
      <div className="border-radius-container flex flex-col items-center justify-center h-[90%] w-full bg-[#FBFAFA] border border-[#818181] shadow-md">
        {loading ? (
          <Skeleton isActive={loading} repeat={2} />
        ) : (
          <div className="w-full h-full items-center justify-center grid grid-cols-[10%,15%,15%,8%,15%,15%,15%] ">
            {cryptoData.map((item, index) => (
              <React.Fragment key={index}>
                <div className="flex items-center justify-center relative right-3">
                  <img
                    src={item.icon}
                    alt={`${item.symbol} Icon`}
                    className="crypto-icon"
                  />
                </div>
                <div className="flex items-center justify-start relative right-2">
                  <p className="crypto-type-text">{item.symbol}</p>
                </div>
                <div className="flex w-full max-h-[8%] items-center justify-start relative right-2">
                  <p className="crypto-price-text">
                    $
                    {/* {parseFloat(item.price.replace(/[^0-9.-]+/g, "")).toFixed(
											3,
										)} */}
                    {parseFloat(
                      item.price.replace(/[^0-9.-]+/g, '')
                    ).toLocaleString('pt-BR', {
                      minimumFractionDigits: 3,
                      maximumFractionDigits: 3,
                    })}
                  </p>
                </div>
                <div className="flex w-full max-h-10 items-center justify-center">
                  <svg
                    className="crypto-graph-icon"
                    viewBox="0 0 16 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d={
                        item.tendency === 'up'
                          ? 'M4.00007 5.49998L4.00007 4.99998L3.70325 4.99998L3.56112 5.26055L4.00007 5.49998ZM10.5001 5.49998L10.5001 5.99998L10.7072 5.99998L10.8536 5.85353L10.5001 5.49998ZM16.0001 0.499936C16.0001 0.223794 15.7762 -6.23152e-05 15.5001 -6.17277e-05L11.0001 -3.96305e-05C10.7239 -3.81e-05 10.5001 0.22382 10.5001 0.499963C10.5001 0.776105 10.7239 0.999962 11.0001 0.99996L15.0001 0.999941L15.0001 4.99994C15.0001 5.27608 15.2239 5.49994 15.5001 5.49994C15.7762 5.49994 16.0001 5.27608 16.0001 4.99994L16.0001 0.499936ZM1.439 11.2394L4.43901 5.73941L3.56112 5.26055L0.561104 10.7605L1.439 11.2394ZM4.00007 5.99998L10.5001 5.99998L10.5001 4.99998L4.00007 4.99998L4.00007 5.99998ZM10.8536 5.85353L15.8536 0.85349L15.1465 0.146387L10.1465 5.14643L10.8536 5.85353Z'
                          : 'M4.00007 6.50002L4.00007 7.00002L3.70325 7.00002L3.56112 6.73945L4.00007 6.50002ZM10.5001 6.50002L10.5001 6.00002L10.7072 6.00002L10.8536 6.14647L10.5001 6.50002ZM16.0001 11.5001C16.0001 11.7762 15.7762 12.0001 15.5001 12.0001L11.0001 12C10.7239 12 10.5001 11.7762 10.5001 11.5C10.5001 11.2239 10.7239 11 11.0001 11L15.0001 11.0001L15.0001 7.00006C15.0001 6.72392 15.2239 6.50006 15.5001 6.50006C15.7762 6.50006 16.0001 6.72392 16.0001 7.00006L16.0001 11.5001ZM1.439 0.760645L4.43901 6.26059L3.56112 6.73945L0.561104 1.23951L1.439 0.760645ZM4.00007 6.00002L10.5001 6.00002L10.5001 7.00002L4.00007 7.00002L4.00007 6.00002ZM10.8536 6.14647L15.8536 11.1465L15.1465 11.8536L10.1465 6.85357L10.8536 6.14647Z'
                      }
                      fill={item.chartColor}
                    />
                  </svg>
                </div>
                <div className="flex w-full max-h-10 items-center justify-center">
                  {apiData && (
                    <SparkLineChart
                      data={generateChartData(item.symbol)}
                      width={100}
                      height={30}
                      showTooltip={false}
                      showHighlight={false}
                      curve="natural"
                    />
                  )}
                </div>
                <div className="w-full h-full flex items-center justify-center">
                  <button
                    type="button"
                    className="crypto-button text-white py-1 px-2 hover:brightness-75 transition bg-[#1e1e1e]"
                    onClick={() =>
                      alert(
                        '⚙️ Função em Desenvolvimento\n\nEstamos trabalhando para trazer esta funcionalidade até você. Agradecemos sua paciência e compreensão. Por favor, aguarde enquanto concluímos os últimos ajustes.'
                      )
                    }
                  >
                    Ver histórico
                  </button>
                </div>
                <div className="w-full h-full flex items-center justify-center">
                  <button
                    type="button"
                    className="crypto-button text-white py-1 px-2 hover:brightness-75 transition bg-[#191919]"
                    onClick={() =>
                      alert(
                        '⚙️ Função em Desenvolvimento\n\nEstamos trabalhando para trazer esta funcionalidade até você. Agradecemos sua paciência e compreensão. Por favor, aguarde enquanto concluímos os últimos ajustes.'
                      )
                    }
                  >
                    Comprar
                  </button>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      <div>
        <p className="underline w-full text-end text-transparent font-medium ml-auto p-text hover:brightness-75">
          Extrato
        </p>
      </div>
    </div>
  )
}
